import { useEffect, useRef, useState } from 'react'

import { api } from '../api/client'
import type { BitLockerReveal, BitLockerRotationInfo } from '../api/types'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'

/**
 * The BitLocker recovery key dialogs of the console.
 *
 * - `MostraChiave`: the helpdesk flow. It shows which key is about to be read
 *   (machine, volume, Key ID to compare with the recovery screen), asks the
 *   reason, and only then asks the Control Plane for the password. The read is
 *   written down (who, when, from where, why) and e-mailed to the tenant's
 *   recipients; by default the Control Plane also queues the rotation of the
 *   key, and the dialog says what happened to it.
 * - `RuotaChiave`: queues a rotation by hand, with its reason.
 *
 * The password lives only in this dialog's state: it is cleared when the
 * dialog closes and hidden by itself after five minutes.
 */

/** A reason shorter than this is refused by the Control Plane (MIN_REASON). */
const MIN_MOTIVO = 10

/** How long the password stays on screen. */
const DURATA_MS = 5 * 60 * 1000

/** The key a dialog acts on: enough to tell the user which one it is. */
export interface ChiaveBitLocker {
  id: string
  key_id: string
  ci_name?: string | null
  mount: string
}

/** The rotation states that mean «the agent will (or is about to) do it». */
const IN_CORSO = ['queued', 'running', 'waiting_escrow']

/** The Italian label of a rotation state (translated where declared). */
export function etichettaRotazione(status: string): string {
  const etichette: Record<string, string> = {
    queued: t('in coda'),
    running: t('in esecuzione sull’agente'),
    waiting_escrow: t('attende la nuova chiave'),
    done: t('completata'),
    failed: t('non riuscita'),
    cancelled: t('annullata'),
    not_queued: t('non messa in coda'),
  }
  return etichette[status] ?? status
}

/** The badge tone of a rotation state. */
export function tonoRotazione(status: string): 'ok' | 'warn' | 'error' | 'info' | 'muted' {
  switch (status) {
    case 'done':
      return 'ok'
    case 'failed':
      return 'error'
    case 'waiting_escrow':
    case 'not_queued':
      return 'warn'
    case 'queued':
    case 'running':
      return 'info'
    default:
      return 'muted'
  }
}

/** Closes on Esc, the way every dialog of the console does. The latest
 *  callback is kept in a ref, so the listener is added once. */
function useEsc(onChiudi: () => void) {
  const ultimo = useRef(onChiudi)
  ultimo.current = onChiudi
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') ultimo.current() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [])
}

/** Machine, volume and Key ID: what the user must check before reading. */
function Identita({ chiave }: { chiave: ChiaveBitLocker }) {
  return (
    <dl className="kv" style={{ marginBottom: 16 }}>
      <dt>{t('Macchina')}</dt>
      <dd>{chiave.ci_name || '—'}</dd>
      <dt>{t('Volume')}</dt>
      <dd className="mono">{chiave.mount || '—'}</dd>
      <dt>{t('ID chiave')}</dt>
      <dd className="mono" style={{ fontSize: 20, fontWeight: 700, letterSpacing: '0.08em' }}>{chiave.key_id}</dd>
    </dl>
  )
}

/** mm:ss of what is left, for the auto-hide countdown. */
function restano(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Reads a recovery key: reason first, then the 48 digits, then what happens to
 * the key (it is no longer secret).
 */
export function MostraChiave({
  chiave,
  onChiudi,
  onLetta,
}: {
  chiave: ChiaveBitLocker
  onChiudi: () => void
  onLetta?: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  // The password apart from the rest of the answer: hiding it must not hide
  // what happened to the rotation.
  const [password, setPassword] = useState<string | null>(null)
  const [esito, setEsito] = useState<Omit<BitLockerReveal, 'recovery_password'> | null>(null)
  const [scadenza, setScadenza] = useState<number | null>(null)
  const [adesso, setAdesso] = useState(() => Date.now())
  const [nascosta, setNascosta] = useState(false)
  const [copiata, setCopiata] = useState<'si' | 'no' | null>(null)
  const [rotazione, setRotazione] = useState<BitLockerRotationInfo | null>(null)
  const [ruotando, setRuotando] = useState(false)
  const [erroreRotazione, setErroreRotazione] = useState<unknown>(null)
  const timerCopia = useRef<number | undefined>(undefined)

  // Closing clears the password before the dialog goes away.
  const chiudi = () => {
    setPassword(null)
    setScadenza(null)
    onChiudi()
  }
  useEsc(() => { if (!lavoro) chiudi() })

  // Auto-hide: after five minutes the password leaves the screen; reading it
  // again is a new read, written down like the first.
  useEffect(() => {
    if (scadenza === null) return
    const id = window.setInterval(() => {
      const ora = Date.now()
      setAdesso(ora)
      if (ora >= scadenza) {
        setPassword(null)
        setScadenza(null)
        setNascosta(true)
      }
    }, 1000)
    return () => window.clearInterval(id)
  }, [scadenza])

  useEffect(() => () => window.clearTimeout(timerCopia.current), [])

  const motivoOk = motivo.trim().length >= MIN_MOTIVO

  async function leggi() {
    if (!motivoOk) return
    setLavoro(true)
    setErrore(null)
    try {
      const r = await api.revealBitLockerKey(chiave.id, motivo.trim())
      const { recovery_password: pw, ...resto } = r
      setPassword(pw)
      setEsito(resto)
      setNascosta(false)
      const ora = Date.now()
      setAdesso(ora)
      setScadenza(ora + DURATA_MS)
      onLetta?.()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  async function copia() {
    if (!password) return
    window.clearTimeout(timerCopia.current)
    try {
      await navigator.clipboard.writeText(password)
      setCopiata('si')
      timerCopia.current = window.setTimeout(() => setCopiata(null), 2500)
    } catch {
      // The plain line below stays until the password is hidden.
      setCopiata('no')
    }
  }

  async function ruotaAdesso() {
    if (!window.confirm(t('Ruotare adesso la chiave di ripristino? La chiave di prima smette di funzionare appena l’agente la sostituisce. Non si può annullare.'))) return
    setRuotando(true)
    setErroreRotazione(null)
    try {
      setRotazione(await api.rotateBitLockerKey(chiave.id, motivo.trim()))
      onLetta?.()
    } catch (e) {
      setErroreRotazione(e)
    } finally {
      setRuotando(false)
    }
  }

  const gruppi = password ? password.split('-') : []

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Chiave di ripristino BitLocker')}>
      <div className="dob-modal__scrim" onClick={lavoro ? undefined : chiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(720px, 100%)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Chiave di ripristino BitLocker')}</h3>
            <p className="dob-modal__sotto">
              {[chiave.ci_name, chiave.mount].filter(Boolean).join(' · ') || chiave.key_id}
            </p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={chiudi} disabled={lavoro}>{t('Chiudi')}</button>
          </div>
        </div>

        <div className="dob-modal__corpo">
          <Identita chiave={chiave} />

          {!esito && (
            <form onSubmit={(e) => { e.preventDefault(); void leggi() }}>
              <div className="banner banner--info" style={{ marginBottom: 16 }}>
                {t('Prima di leggere, controlla che l’ID chiave mostrato dalla schermata di ripristino BitLocker sia {id}. La lettura viene registrata (chi, quando, da dove e perché) e notificata per e-mail ai destinatari dell’evento BitLocker; di norma, dopo la lettura, l’agente sostituisce la chiave.', { id: chiave.key_id })}
              </div>
              <div className="field">
                <label htmlFor="bitlocker-motivo">{t('Motivo della lettura')}</label>
                <textarea
                  id="bitlocker-motivo"
                  rows={3}
                  autoFocus
                  autoComplete="off"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder={t('ticket 4711, utente bloccato alla schermata di ripristino')}
                />
                <div className="field__hint">
                  {t('Almeno {n} caratteri: il motivo resta nel registro delle letture e compare nell’e-mail di avviso.', { n: MIN_MOTIVO })}
                </div>
              </div>
              <ErrorBanner error={errore} />
              <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 0 }}>
                <button type="button" className="secondary" onClick={chiudi} disabled={lavoro}>{t('Annulla')}</button>
                <button type="submit" disabled={!motivoOk || lavoro}>
                  {lavoro ? t('Leggo…') : t('Mostra la chiave')}
                </button>
              </div>
            </form>
          )}

          {esito && (
            <>
              {password ? (
                <>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <span>{t('Password di ripristino')}</span>
                    <div
                      className="mono"
                      aria-label={t('Password di ripristino')}
                      style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px 14px', padding: '16px 18px',
                        background: 'var(--surface-sunken)', border: '1px solid var(--divider)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      {gruppi.map((g, i) => (
                        <span key={i} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '0.06em' }}>
                            {g}{i < gruppi.length - 1 ? <span className="text-muted">-</span> : null}
                          </span>
                          <small className="text-muted" style={{ fontSize: 11, userSelect: 'none' }}>{i + 1}</small>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="toolbar" style={{ alignItems: 'center', marginBottom: 12 }}>
                    <button type="button" onClick={() => void copia()}>{t('Copia')}</button>
                    {copiata === 'si' && <span className="badge badge--ok">{t('copiata negli appunti')}</span>}
                    {copiata === 'no' && <span className="text-muted">{t('Copia non disponibile qui: seleziona il testo qui sotto e copialo a mano.')}</span>}
                    {scadenza !== null && (
                      <span className="text-muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
                        {t('Si nasconde da sola fra {tempo}', { tempo: restano(scadenza - adesso) })}
                      </span>
                    )}
                  </div>
                  {copiata === 'no' && (
                    // Without the clipboard API (plain http) the password is
                    // offered as one plain line, ready to select.
                    <input type="text" readOnly className="mono" value={password} autoFocus
                      onFocus={(e) => e.target.select()} style={{ marginBottom: 12 }}
                      aria-label={t('Password di ripristino')} />
                  )}
                  <div className="banner banner--warn" style={{ marginBottom: 12 }}>
                    {t('Questa chiave non è più segreta: chi l’ha vista o ascoltata può sbloccare il volume. Non scriverla nel ticket e non inviarla per e-mail.')}
                  </div>
                </>
              ) : (
                <div className="banner banner--info" style={{ marginBottom: 12 }}>
                  {nascosta
                    ? t('La chiave è stata nascosta dopo 5 minuti. Per rivederla serve una nuova lettura, registrata come la prima.')
                    : t('La chiave è stata nascosta.')}
                </div>
              )}

              <EsitoRotazione
                esito={esito}
                rotazione={rotazione}
                ruotando={ruotando}
                onRuota={() => void ruotaAdesso()}
              />
              <ErrorBanner error={erroreRotazione} />

              <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 0, marginTop: 12 }}>
                <button type="button" onClick={chiudi}>{t('Chiudi')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/** What happens to the key now that it has been read. */
function EsitoRotazione({
  esito,
  rotazione,
  ruotando,
  onRuota,
}: {
  esito: Omit<BitLockerReveal, 'recovery_password'>
  rotazione: BitLockerRotationInfo | null
  ruotando: boolean
  onRuota: () => void
}) {
  // A rotation queued by hand from this dialog wins over the reveal's answer.
  const r = rotazione ?? esito.rotation
  if (r && IN_CORSO.includes(r.status)) {
    return (
      <div className="banner banner--ok">
        {r.status === 'queued'
          ? t('La chiave verrà sostituita dall’agente appena la macchina è in linea: prima aggiunge una nuova chiave di ripristino, poi — quando CENSO l’ha custodita — toglie questa.')
          : t('La sostituzione della chiave è già in corso: {stato}.', { stato: etichettaRotazione(r.status) })}
      </div>
    )
  }
  if (r && r.status === 'not_queued') {
    return (
      <div className="banner banner--warn">
        <strong>{t('La sostituzione automatica non è partita.')}</strong> {r.log ? t(r.log) : ''}
      </div>
    )
  }
  if (r) {
    return (
      <div className="banner banner--info">
        {t('Rotazione: {stato}.', { stato: etichettaRotazione(r.status) })}
      </div>
    )
  }
  if (esito.status !== 'attiva') {
    return (
      <div className="banner banner--info">
        {t('Questa chiave è già stata sostituita sul volume: non c’è niente da ruotare.')}
      </div>
    )
  }
  return (
    <div className="banner banner--warn" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ flex: '1 1 320px' }}>
        {t('La rotazione dopo la lettura è spenta: la chiave resta quella di prima finché qualcuno non la sostituisce.')}
      </span>
      <button type="button" onClick={onRuota} disabled={ruotando}>
        {ruotando ? t('Metto in coda…') : t('Ruota adesso')}
      </button>
    </div>
  )
}

/**
 * Queues the rotation of a key by hand: the agent adds a new recovery
 * password and, once CENSO holds it, removes the old one.
 */
export function RuotaChiave({
  chiave,
  onChiudi,
  onFatto,
}: {
  chiave: ChiaveBitLocker
  onChiudi: () => void
  onFatto: (r: BitLockerRotationInfo) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  useEsc(() => { if (!lavoro) onChiudi() })

  async function ruota() {
    if (!motivo.trim()) return
    setLavoro(true)
    setErrore(null)
    try {
      onFatto(await api.rotateBitLockerKey(chiave.id, motivo.trim()))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Ruota la chiave di ripristino')}>
      <div className="dob-modal__scrim" onClick={lavoro ? undefined : onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(620px, 100%)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Ruota la chiave di ripristino')}</h3>
            <p className="dob-modal__sotto">
              {t('L’agente aggiunge una nuova password di ripristino e, appena CENSO l’ha custodita, toglie questa: la macchina non resta mai senza una chiave custodita.')}
            </p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Chiudi')}</button>
          </div>
        </div>
        <div className="dob-modal__corpo">
          <Identita chiave={chiave} />
          <form onSubmit={(e) => { e.preventDefault(); void ruota() }}>
            <div className="field">
              <label htmlFor="bitlocker-motivo-rotazione">{t('Motivo della rotazione')}</label>
              <textarea
                id="bitlocker-motivo-rotazione"
                rows={2}
                autoFocus
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={t('chiave comunicata al telefono, rotazione periodica…')}
              />
              <div className="field__hint">
                {t('Serve la raccolta delle chiavi accesa e l’agente CENSO sulla macchina; si ruotano i volumi con una lettera di unità.')}
              </div>
            </div>
            <ErrorBanner error={errore} />
            <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 0 }}>
              <button type="button" className="secondary" onClick={onChiudi} disabled={lavoro}>{t('Annulla')}</button>
              <button type="submit" disabled={!motivo.trim() || lavoro}>
                {lavoro ? t('Metto in coda…') : t('Ruota')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
